// src/pages/SurveyPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { questions, dimensions } from '../data/surveyData';
import { FaArrowRight, FaArrowLeft, FaCheck } from 'react-icons/fa';

const SurveyPage = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  
  const [surveyData, setSurveyData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [eNPS, setENPS] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Check if already submitted
  useEffect(() => {
    const checkSubmission = () => {
      const submissionKey = `survey_${surveyId}_submitted`;
      const hasSubmitted = localStorage.getItem(submissionKey);
      if (hasSubmitted) {
        setSubmitted(true);
      }
    };
    checkSubmission();
  }, [surveyId]);

  // Load survey data
  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const q = query(collection(db, 'surveys'), where('id', '==', surveyId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const survey = querySnapshot.docs[0].data();
          setSurveyData(survey);
        } else {
          alert('الاستبيان غير موجود');
          navigate('/');
        }
      } catch (error) {
        console.error('Error fetching survey:', error);
        alert('حدث خطأ في تحميل الاستبيان');
      } finally {
        setLoading(false);
      }
    };

    fetchSurvey();
  }, [surveyId, navigate]);

  // Load saved progress from localStorage
  useEffect(() => {
    const savedAnswers = localStorage.getItem(`survey_${surveyId}_answers`);
    const savedENPS = localStorage.getItem(`survey_${surveyId}_enps`);
    const savedQuestion = localStorage.getItem(`survey_${surveyId}_question`);
    
    if (savedAnswers) {
      setAnswers(JSON.parse(savedAnswers));
    }
    if (savedENPS) {
      setENPS(savedENPS);
    }
    if (savedQuestion) {
      setCurrentQuestion(parseInt(savedQuestion));
    }
  }, [surveyId]);

  // Save progress to localStorage
  const saveProgress = () => {
    localStorage.setItem(`survey_${surveyId}_answers`, JSON.stringify(answers));
    localStorage.setItem(`survey_${surveyId}_enps`, eNPS);
    localStorage.setItem(`survey_${surveyId}_question`, currentQuestion.toString());
  };

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [currentQuestion]: value };
    setAnswers(newAnswers);
    
    // Auto advance to next question after a short delay
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      }
    }, 300);
    
    // Save progress
    localStorage.setItem(`survey_${surveyId}_answers`, JSON.stringify(newAnswers));
    localStorage.setItem(`survey_${surveyId}_question`, (currentQuestion + 1).toString());
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      saveProgress();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      saveProgress();
    }
  };

  const calculateScores = () => {
    const dimensionScores = {};
    let totalScore = 0;
    let answeredCount = 0;

    // Initialize dimension scores
    dimensions.forEach(dim => {
      dimensionScores[dim.id] = { sum: 0, count: 0 };
    });

    // Calculate scores
    questions.forEach((question, index) => {
      const answer = answers[index];
      if (answer) {
        let score = answer;
        // Reverse scoring for reverse questions
        if (question.reverse) {
          score = 6 - answer;
        }
        
        dimensionScores[question.dimension].sum += score;
        dimensionScores[question.dimension].count += 1;
        totalScore += score;
        answeredCount += 1;
      }
    });

    // Calculate averages
    const results = {};
    dimensions.forEach(dim => {
      if (dimensionScores[dim.id].count > 0) {
        results[dim.id] = dimensionScores[dim.id].sum / dimensionScores[dim.id].count;
      }
    });

    const overallScore = answeredCount > 0 ? totalScore / answeredCount : 0;

    return { dimensionScores: results, overallScore };
  };

  const handleSubmit = async () => {
    // التحقق من إكمال جميع الأسئلة
    const allQuestionsAnswered = questions.every((_, index) => answers[index] !== undefined);
    
    if (!allQuestionsAnswered || !eNPS) {
      alert('يرجى الإجابة على جميع الأسئلة قبل الإرسال');
      return;
    }

    setSubmitting(true);
    try {
      const { dimensionScores, overallScore } = calculateScores();
      
      // Prepare response data
      const responseData = {
        surveyId,
        answers: questions.map((_, index) => answers[index]),
        eNPS: parseInt(eNPS),
        dimensionScores,
        overallScore,
        submittedAt: new Date(),
        sessionId: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };

      // Save to Firebase
      await addDoc(collection(db, 'responses'), responseData);

      // Mark as submitted
      localStorage.setItem(`survey_${surveyId}_submitted`, 'true');
      
      // Clear saved progress
      localStorage.removeItem(`survey_${surveyId}_answers`);
      localStorage.removeItem(`survey_${surveyId}_enps`);
      localStorage.removeItem(`survey_${surveyId}_question`);

      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting survey:', error);
      alert('حدث خطأ في إرسال الاستبيان. يرجى المحاولة مرة أخرى.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <div className="bg-green-50 border border-green-200 rounded-lg p-8">
          <FaCheck className="text-5xl text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-green-800 mb-2">
            شكراً لك!
          </h2>
          <p className="text-green-700 mb-4">
            تم إرسال إجاباتك بنجاح. نقدر وقتك ومشاركتك في تحسين بيئة العمل.
          </p>
          <p className="text-gray-600">
            يمكنك الآن إغلاق هذه الصفحة
          </p>
        </div>
      </div>
    );
  }

  const progress = ((Object.keys(answers).length / questions.length) * 100).toFixed(0);
  const currentQ = questions[currentQuestion];
  const currentDimension = dimensions.find(d => d.id === currentQ?.dimension);

  // Check if we're on the eNPS question
  const isENPSQuestion = currentQuestion >= questions.length;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          استبيان السعادة الوظيفية
        </h1>
        {surveyData && (
          <p className="text-xl text-gray-600">
            {surveyData.organizationName}
          </p>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>التقدم: {progress}%</span>
          <span>
            {isENPSQuestion 
              ? 'سؤال إضافي' 
              : `السؤال ${currentQuestion + 1} من ${questions.length}`}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-primary-green h-3 rounded-full progress-bar transition-all duration-300"
            style={{ width: `${Math.min(((currentQuestion + 1) / (questions.length + 1)) * 100, 100)}%` }}
          />
        </div>
      </div>

      {/* Question Card */}
      {!isENPSQuestion ? (
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6 slide-in">
          {/* Dimension Badge */}
          <div className="mb-6">
            <span 
              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white"
              style={{ backgroundColor: currentDimension?.color }}
            >
              {currentDimension?.name}
            </span>
          </div>

          {/* Question */}
          <h2 className="text-2xl font-medium text-gray-900 mb-8">
            {currentQ.text}
          </h2>

          {/* Answer Options - ترتيب معكوس */}
          <div className="space-y-3">
            {[
              { value: 5, label: 'أوافق بشدة' },
              { value: 4, label: 'أوافق' },
              { value: 3, label: 'محايد' },
              { value: 2, label: 'لا أوافق' },
              { value: 1, label: 'لا أوافق بشدة' }
            ].map(option => (
              <label 
                key={option.value}
                className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                style={{
                  borderColor: answers[currentQuestion] === option.value ? currentDimension?.color : '#e5e7eb',
                  backgroundColor: answers[currentQuestion] === option.value ? `${currentDimension?.color}10` : 'white'
                }}
              >
                <input
                  type="radio"
                  name={`question-${currentQuestion}`}
                  value={option.value}
                  checked={answers[currentQuestion] === option.value}
                  onChange={() => handleAnswer(option.value)}
                  className="radio-custom ml-3"
                />
                <span className="text-lg">{option.label}</span>
              </label>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${
                currentQuestion === 0 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <FaArrowRight className="ml-2" />
              السابق
            </button>

            {currentQuestion < questions.length - 1 ? (
              <button
                onClick={handleNext}
                disabled={!answers[currentQuestion]}
                className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${
                  !answers[currentQuestion] 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-primary-green text-white hover:bg-green-700'
                }`}
              >
                التالي
                <FaArrowLeft className="mr-2" />
              </button>
            ) : (
              <button
                onClick={() => setCurrentQuestion(currentQuestion + 1)}
                disabled={!answers[currentQuestion]}
                className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${
                  !answers[currentQuestion] 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-primary-gold text-white hover:bg-yellow-700'
                }`}
              >
                سؤال إضافي
                <FaArrowLeft className="mr-2" />
              </button>
            )}
          </div>
        </div>
      ) : (
        // eNPS Question
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h2 className="text-2xl font-medium text-gray-900 mb-8">
            على مقياس من 0 إلى 10، ما مدى احتمالية أن توصي بمنظمتك كمكان عمل لصديق؟
          </h2>
          
          <div className="grid grid-cols-11 gap-2 mb-6">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
              <button
                key={num}
                onClick={() => setENPS(num.toString())}
                className={`py-4 rounded-lg font-medium transition-all ${
                  eNPS === num.toString()
                    ? 'bg-primary-green text-white transform scale-110'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {num}
              </button>
            ))}
          </div>

          <div className="flex justify-between text-sm text-gray-600">
            <span>لن أوصي إطلاقاً</span>
            <span>سأوصي بشدة</span>
          </div>

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentQuestion(currentQuestion - 1)}
              className="flex items-center px-6 py-3 rounded-lg font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
            >
              <FaArrowRight className="ml-2" />
              السابق
            </button>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!eNPS || submitting}
              className={`px-8 py-4 rounded-lg font-medium text-lg transition-all ${
                !eNPS || submitting
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 transform hover:scale-105'
              }`}
            >
              {submitting ? (
                <div className="flex items-center">
                  <div className="spinner ml-3"></div>
                  جاري الإرسال...
                </div>
              ) : (
                <>
                  <FaCheck className="inline ml-2" />
                  إرسال الاستبيان
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Quick Navigation */}
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-sm text-gray-600 mb-3">الانتقال السريع:</p>
        <div className="flex flex-wrap gap-2">
          {questions.map((q, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentQuestion(idx)}
              className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                answers[idx] 
                  ? 'bg-green-500 text-white' 
                  : idx === currentQuestion
                  ? 'bg-primary-green text-white'
                  : 'bg-white text-gray-600 border border-gray-300'
              }`}
            >
              {idx + 1}
            </button>
          ))}
          {/* eNPS button */}
          <button
            onClick={() => setCurrentQuestion(questions.length)}
            className={`h-10 px-3 rounded-lg text-sm font-medium transition-colors ${
              eNPS 
                ? 'bg-green-500 text-white' 
                : currentQuestion === questions.length
                ? 'bg-primary-gold text-white'
                : 'bg-white text-gray-600 border border-gray-300'
            }`}
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyPage;
